import { useEffect } from "react";
import type { AppPage } from "../context/MonyawnContext.types";
import { buildSchemasForPage } from "../seo/schema";
import { getSeoForPage } from "../seo/routes";
import { BRAND_NAME, DEFAULT_OG_IMAGE, SITE_URL, toAbsoluteUrl } from "../seo/siteConfig";

const SCHEMA_SCRIPT_PREFIX = "monyawn-schema-";

function upsertMetaByName(name: string, content: string) {
  let tag = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  let tag = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(url: string) {
  let link = document.head.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function clearManagedSchemas() {
  const scripts = document.head.querySelectorAll(`script[id^='${SCHEMA_SCRIPT_PREFIX}']`);
  scripts.forEach((script) => script.remove());
}

function injectSchemas(page: AppPage) {
  clearManagedSchemas();
  const schemas = buildSchemasForPage(page);

  schemas.forEach((schema, index) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = `${SCHEMA_SCRIPT_PREFIX}${index}`;
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

function getRouteUrl(page: AppPage): string {
  if (page === "login") return SITE_URL;
  return `${SITE_URL}/#${page}`;
}

export function useRouteSeo(page: AppPage) {
  useEffect(() => {
    const routeSeo = getSeoForPage(page);
    const canonicalUrl = toAbsoluteUrl(routeSeo.canonicalPath);
    const routeUrl = getRouteUrl(page);

    document.title = routeSeo.title;

    upsertMetaByName("description", routeSeo.description);
    upsertMetaByName("robots", routeSeo.robots);
    upsertMetaByName("application-name", BRAND_NAME);
    upsertMetaByName("apple-mobile-web-app-title", BRAND_NAME);

    upsertMetaByProperty("og:type", "website");
    upsertMetaByProperty("og:site_name", BRAND_NAME);
    upsertMetaByProperty("og:title", routeSeo.title);
    upsertMetaByProperty("og:description", routeSeo.description);
    upsertMetaByProperty("og:url", routeUrl);
    upsertMetaByProperty("og:image", DEFAULT_OG_IMAGE);

    upsertMetaByProperty("twitter:card", "summary_large_image");
    upsertMetaByProperty("twitter:title", routeSeo.title);
    upsertMetaByProperty("twitter:description", routeSeo.description);
    upsertMetaByProperty("twitter:image", DEFAULT_OG_IMAGE);

    upsertCanonical(canonicalUrl);
    injectSchemas(page);

    return () => {
      clearManagedSchemas();
    };
  }, [page]);
}
